import React, { useMemo, useState } from 'react';
import {
	View,
	ScrollView,
	StyleSheet,
	Image,
} from 'react-native';
import { useSelector } from 'react-redux';
const imageEmptyState = require('../../../../assets/images/reportEmptyState/report-empty.png');
const clockInLogo = require('../../../../assets/images/clockLogo/michpal_clock_in.svg');
import { convertFromUtc, Props } from './Clock.utils';
import { Record } from './Record/Record.component';
import colors from '../../../config/colors';
import I18n from '../i18n/I18n.component';
import SelectFilter from './EventsAndProjectsSelect/EventsAndProjectsSelect.container';
import Timer from '../Timer/Timer.container';
import Typography from '../Typography/Typography.component';
import ClockDisplay from './ClockDisplay/ClockDisplay.component';
import RecordCard from './Record/Record.component';
import ActionButton from '../Buttons/ActionButton.container';
import moment from 'moment';
import { getServerDateTime } from '../../../store/selectors/dateTime.selectors';
import { i18nService } from '../../../services/i18n.service';
import { Platform } from '@unimodules/core';
import { toNumber } from 'lodash';
import ImageIcon from '../Icon/ImageIcon.component';

const ClockReportComponent = ({
	initialTime,
	records,
	shiftTime,
	totalReported,
	activeDayTotalReported,
	reachedMax,
	onPressEntry,
	onPressExit,
	onPressReportExit,
	projectsOptions,
	eventsOptions,
	onSelectProjectOrEvent,
	selectedItem,
	defaultProjectOption,
	defaultEventOption,
	label,
	canReportFullDay,
	disableButtons,
	standardHours,
	onDelete,
	onEdit,
	disableEntryButton,
	selectedItemType,
	handleOpenOptions,
	eventPlaceholdar,
	currentRunnigWorkLog,
	openProjectPicker,
	splitCards,
}: Props) => {
	const currentServerTime = useSelector(getServerDateTime);
	const todayDate = convertFromUtc(currentServerTime, false, true, 'YYYY-MM-DDTHH:mm', 'DD/MM/YYYY');
	const isToday = todayDate === moment().format('DD/MM/YYYY');

	const convertHoursFormat = (
		minutesValue: any,
		format?: string,
		inminutes?: boolean,
	) => {
		if (minutesValue) {
			const value = inminutes
				? minutesValue
				: Math.floor(minutesValue * 60);
			if (format === 'HH:mm') {
				const hours = `${Math.floor(value / 60)?.toLocaleString()}`;
				const minutes =
					value % 60 !== 0
						? value % 60 < 10
							? `0${value % 60}`
							: `${value % 60}`
						: '00';
				return `${hours}:${minutes}`;
			}
			return `${(value / 60).toFixed(2)}`;
		}
		return '00:00';
	};

	const convertToHoursFormat = time => {
		if (!time) return '00:00';
		const [hours, minutes, seconds] = time.split(':');
		return `${toNumber(hours).toLocaleString()}:${minutes}`;
	};

	const isTotalReportedSmallerThanStandard = useMemo(() => {
		let standard = 0;
		if (standardHours?.fixHours) {
			standard =
				moment(standardHours.fixHours, 'HH:mm:ss').diff(
					moment('00:00', 'HH:mm'),
					'minutes',
				) / 60;
		}

		return totalReported < standard;
	}, [totalReported, standardHours]);
	const standard = useMemo(() => {
		return convertToHoursFormat(standardHours?.fixHours);
	}, [standardHours]);

	const clockSection = (
		<>
			<View style={styles.clockContainer}>

				{!disableButtons.disable ? (
					<View nativeID='FiltersHolder' style={styles.FiltersHolder}>
						{eventsOptions.length ? (
							<SelectFilter //events select
								canReportFullDay={canReportFullDay}
								options={eventsOptions}
								isProject={false}
								placeHolder={eventPlaceholdar}
								onSubmit={onSelectProjectOrEvent}
								disabled={selectedItemType === 'projects'}
								onOpen={isOpen =>
									handleOpenOptions(isOpen, 'events')
								}
								defaultOption={defaultEventOption}
								currentRunnigWorkLog={currentRunnigWorkLog}
							/>
						) : null}
						{projectsOptions.length ? (
							<SelectFilter //projects select
								canReportFullDay={canReportFullDay}
								options={projectsOptions}
								isProject={true}
								placeHolder={
									defaultProjectOption.name
										? defaultProjectOption.name
										: 'menu.projects'
								}
								onSubmit={onSelectProjectOrEvent}
								disabled={selectedItemType === 'events'}
								onOpen={isOpen =>
									handleOpenOptions(isOpen, 'projects')
								}
								defaultOption={defaultProjectOption}
								currentRunnigWorkLog={currentRunnigWorkLog}
							/>
							) : null}
						</View>
					) : null}

				<ClockDisplay
					isRunning={!!initialTime}
					initialTime={initialTime}
					shiftTime={shiftTime}
					totalReported={activeDayTotalReported}
					reachedMax={reachedMax}
					enterTime={currentRunnigWorkLog?.enterTime}
					isFullDayReport={disableButtons.disable}
				/>

				{label ? (
					<Typography
						size={14}
						color='primaryDarkBlue'
						weight='normal'
						style={{ marginVertical: 10, textAlign: 'center' }}
					>
						{label}
					</Typography>
				) : null}

				{!disableButtons.disable ? (
					<View nativeID='ButtonsHolder' style={styles.ButtonsHolder}>
						{initialTime ? (
							<>
								<ActionButton onPress={onPressExit} overrideStyle={styles.filledButton} hoverStyle={{backgroundColor: colors.primaryDark}}>
									<I18n size={16} weight='normal' color='white'>
										general.exit
									</I18n>
									<ImageIcon name='stop' size={16} color='white' style={{ marginRight: 4 }} />
								</ActionButton>
								{projectsOptions.length ? (
									<ActionButton onPress={() => openProjectPicker?.()} overrideStyle={styles.outlinedButton}>
										<Typography size={16} weight='normal' color='primary'>
											{'+ '}
										</Typography>
										<I18n size={16} weight='normal' color='primary'>
											menu.projects
										</I18n>
									</ActionButton>
								) : null}
							</>
						) : (
							<>
								<ActionButton
									onPress={onPressEntry}
									disabled={disableEntryButton}
									overrideStyle={[styles.filledGreenButton, disableEntryButton && { opacity: 0.3 }]}
									hoverStyle={disableEntryButton ? {} : styles.filledGreenButtonHover}
								>
									<I18n size={16} weight='normal' color='white'>
										general.enterence
									</I18n>
									<ImageIcon name='play' width={18} height={18} color='white' style={{ marginLeft: 4 }} />
								</ActionButton>
								<ActionButton onPress={onPressReportExit ?? onPressExit} overrideStyle={styles.outlinedButton}>
									<I18n size={16} weight='normal' color='primary'>
										general.reportExit
									</I18n>
									<ImageIcon name='clockLoginPrimary' height={18} width={18} style={{ marginLeft: 4 }} />
								</ActionButton>
							</>
						)}
					</View>
				) : (
					<View
						style={{
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							height: 100,
							marginTop: 10,
						}}
					>
						<I18n size={16} weight='normal' color='clockRecordBlue'>
							{`${i18nService.translate('general.youAreNowReportingEvent')} `}
						</I18n>
						<I18n size={16} font='medium' color='titlePrimary'>
							{`${i18nService.translate(disableButtons.eventName)} - יום מלא`}
						</I18n>
					</View>
				)}
			</View>
		</>
	);
	const recordsSection = (
		<View style={{ flex: 1, width: '100%', gap: 16 }}>
			<View style={styles.sectionHeaderRow}>
				<View style={{ flexDirection: 'row-reverse', alignItems: 'center', gap: 4 }}>
					<Typography size={14} color='primaryDarkBlue' font='medium'>
						{i18nService.translate('general.startDay')}
					</Typography>
					<Typography size={14} color='greyText' weight='400'>
						{`${isToday ? 'היום' : todayDate}`}
					</Typography>
				</View>
				<View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
					<Typography size={12} color='clockRecordBlue' font='medium'>
						{convertHoursFormat(totalReported, 'HH:mm', false)}
					</Typography>
					<Typography size={12} color='greyText' weight='normal'>
						{`/${standard}`}
					</Typography>
				</View>
			</View>

			{records.length ? (
				<>
					<ScrollView nativeID='tableHolder' style={styles.tableHolder}>
						{records.map((record: Record) => (
							<RecordCard
								onDelete={onDelete}
								onEdit={onEdit}
								key={record.workLogId}
								record={record}
							/>
						))}
					</ScrollView>
				</>
			) : (
				<View style={styles.emptyState}>
					<ImageIcon name='noRecords' width={120} height={120} />
					<I18n size={14} color='greyText' weight='normal'>
						reportView.noWorklogsToday
					</I18n>
				</View>
			)}
		</View>
	);

	if (splitCards) {
		return (
			<>
				<View style={styles.splitCardsRow}>
					<View style={styles.contentBlock} nativeID='clockCard'>
						<View style={styles.logoBlock}>
							<Typography size={14} weight='bold' color='primaryDarkBlue'>
								{i18nService.translate('attendance.clockTitle')}
							</Typography>
						</View>
						{clockSection}
					</View>
					<View style={[styles.contentBlock]} nativeID='recordsCard'>
						{recordsSection}
					</View>
				</View>
			</>
		);
	}

	return (
		<View style={styles.contentBlock} nativeID='clockReport'>
			<View style={styles.logoBlock}>
				<Image
					source={clockInLogo}
					fadeDuration={0}
					style={{ height: 30, width: 108 }}
				/>
			</View>
			{clockSection}
			{/* divider */}
			<View style={{ width: '100%', height: 0, borderWidth: 1, borderColor: '#EBF0F6' }} />
			<View style={{ flex: 1, width: '100%', gap: 16 }}>
				{recordsSection}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	contentBlock: {
		flex: 1,
		padding: 24,
		gap: 16,
		backgroundColor: colors.white,
		borderRadius: 8,
		boxShadow: '2px 2px 16px 0 rgba(0, 0, 0, 0.04)',
		width: '100%',
		alignItems: 'center',
	},
	logoBlock: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		alignItems: 'center',
		width: '100%',
	},
	clockContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		paddingVertical: 16,
		gap: 16,
		width: '100%',
	},
	sectionHeaderRow: {
		flexDirection: 'row-reverse',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 8,
	},
	dividerLine: {
		height: 1,
		backgroundColor: '#E0E0E0',
		marginBottom: 8,
	},
	columnHeaderRow: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: 6,
		borderBottomWidth: 1,
		borderBottomColor: '#E2EDF4',
	},
	columnHeader: {
		flex: 1,
		alignItems: 'center',
	},
	totalRow: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		paddingVertical: 8,
		borderTopWidth: 1,
		borderTopColor: '#E0E0E0',
	},
	emptyState: {
		alignItems: 'center',
		justifyContent: 'center',
		paddingVertical: 40,
		gap: 24,
	},
	emptyStateSmallStars: {
		flexDirection: 'row',
		marginTop: 4,
	},
	detailsHeader: {
		width: '100%',
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginBottom: 20,
		paddingRight: 30,
	},
	FiltersHolder: {
		justifyContent: 'center',
		alignItems: 'center',
		height: 42,
		marginBottom: 10,
		flexDirection: 'row',
		width: '100%',
		zIndex: 11111,
	},
	ButtonsHolder: {
		justifyContent: 'center',
		alignItems: 'center',
		flexDirection: 'row-reverse',
		gap: 12,
	},
	filledGreenButton: {
		// flexDirection: 'row',
		// alignItems: 'center',
		// justifyContent: 'center',
		// paddingHorizontal: 24,
		// height: 44,
		// borderRadius: 8,
		backgroundColor: colors.greenClock,
	},
	filledGreenButtonHover: {
		backgroundColor: colors.greenClockHover,
	},
	filledButton:{
		backgroundColor: colors.primary,
	},
	outlinedButton: {
		borderWidth: 1,
		borderColor: colors.titlePrimary,
	},
	tableHolder: {
		width: '100%',
		minHeight: 140,
		direction: 'ltr',
		//paddingRight: 15,
	},
	splitCardsRow: {
		flexDirection: 'row-reverse',
		flex: 1,
		gap: 24,
	},
});
export default ClockReportComponent;
